import { Component, OnInit, Input, ElementRef, AfterViewInit, EventEmitter, Output, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';

import { SECTION_ANIMATIONS } from './section.animations';
import { SectionService } from './section.service';
import { SECTION_ICONS } from './section.constants';

@Component({
    selector: 'finder-section',
    templateUrl: './section.component.html',
    styleUrls: ['./section.component.scss'],
    animations: [...SECTION_ANIMATIONS]
})
export class SectionComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() deepLink: string;
    @Input() expanded: boolean;
    @Input() fragments: Array<string>;
    @Output() fragmentsChange = new EventEmitter<Array<string>>();
    @Input() icons: any = SECTION_ICONS;
    @Input() id: Readonly<string>;
    @Input() labels: any;
    @Input() locationTitle: string;
    @Input() sync = false;
    @Input() headerIcon: string;
    @Input() disableDeepLink = false;

    @ViewChild('contentElement', { read: ElementRef }) contentElement: ElementRef;

    title: string;
    ariaLabel: string;
    iconOpened: string;
    iconClosed: string;

    private destroy$ = new Subject<void>();

    constructor(
        private location: Location,
        private route: ActivatedRoute,
        private sectionService: SectionService
    ) { }

    ngOnInit() {
        this.title = this.labels.sectionTitle;
        this.ariaLabel = this.locationTitle ? `${this.locationTitle}: ${this.title}` : this.title;
        this.setSectionIcons();

        this.route.fragment
            .pipe(takeUntil(this.destroy$))
            .subscribe(fragment => {
                if (this.disableDeepLink) {
                    return;
                }

                this.checkFragment(fragment);
            });

        // Listen for the open/close request
        if (this.sync) {
            this.sectionService.clickEventObservable()
                .pipe(takeUntil(this.destroy$))
                .subscribe((event) => {
                    this.expanded = get(event, 'id') === this.id;
                    this.updateComponent();
                });
        }
    }

    ngAfterViewInit() {
        this.checkSectionContent();
    }

    // Ignore the if just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggle(event) {
        // Stop propagation to avoid closing parent sections.
        event.stopPropagation();
        // Only do this if we're trying to sync multiple groups
        if (this.sync) {
            this.sectionService.sectionClicked(this.id, this.expanded, !this.expanded);

            return;
        }

        this.expanded = !this.expanded;

        if (this.expanded) {
            this.contentElement.nativeElement.classList.remove('shrunk');
        }

        this.updateComponent();
    }

    setSectionIcons() {
        this.iconOpened = get(this.icons, 'name.opened');
        this.iconClosed = get(this.icons, 'name.closed');
    }

    checkFragment(fragment) {
        if (fragment) {
            this.fragments = fragment.split(',');
            this.expanded = fragment.includes(this.deepLink);
            this.checkURL();
        }
    }

    checkURL() {
        // If we're not changing the URI just stop
        if (!this.fragments) {
            return;
        }

        // URL without hashes.
        let url = this.location.path();

        // Add/Remove the deep link from the fragments array.
        if (this.expanded) {
            if (!this.fragments.includes(this.deepLink)) {
                this.fragments.push(this.deepLink);
            }
        } else {
            this.fragments = this.fragments.filter(fragment => fragment !== this.deepLink);
        }

        this.fragmentsChange.emit(this.fragments);

        if (this.fragments.length > 0) {
            url = url.concat('#', this.fragments.join(','));
        }

        this.location.go(url);
    }

    onAnimationEvent($event: any) {
        if ($event.toState === 'shrunk') {
            this.contentElement.nativeElement.classList.add('shrunk');
        } else {
            this.contentElement.nativeElement.classList.remove('shrunk');
        }
    }

    /**
     * Iterate through all the anchors and elements that
     * can be tabbed and set the tabindex value based on
     * the section status.
     */
    private checkSectionContent() {
        // Get all the links and elements that can be tabbed.
        const elements = this.contentElement.nativeElement.querySelectorAll('.anchor, [tabindex]');

        if (this.expanded) {
            elements.forEach(element => element.setAttribute('tabindex', '0'));
        } else {
            elements.forEach(element => element.setAttribute('tabindex', '-1'));
        }
    }

    private updateComponent() {
        this.checkSectionContent();
        this.checkURL();
    }
}
